// src/components/products/ProductTab.js
import React, { useState, useEffect, useRef } from "react";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { productAPI } from "../../services";
import { handleFormattedValidationErrors } from "../../utils";

import "../../style/product.css";
import ProductCard from "./components/ProductCard";
import SearchBar from "./components/SearchBar";
import ProductTable from "./components/ProductTable";
import Pagination from "./components/Pagination";
import AddProductModal from "./components/AddProductModal";
import EditProductModal from "./components/EditProductModal";

import TotalProductsIcon from "../../assets/total-product.svg";
import TotalStockIcon from "../../assets/total-stock.svg";
import TotalInventoryValueIcon from "../../assets/total-inventory.svg";
import SellingPriceIcon from "../../assets/selling-price.svg";
import DeleteIcon from "../../assets/delete-modal-icon.svg";

export default function ProductTab() {
  const navigate = useNavigate();

  // 👉 All product states moved here
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [sortField, ] = useState("");
  const [sortDirection, ] = useState("asc");
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ 
    product_count: 0, 
    available_quantity: 0, 
    inventory_value: 0, 
    inventory_selling_price: 0 
  });
  const [statsLoading, setStatsLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(null);

  // Ref to track if initial load has been done
  const initialLoadDone = useRef(false);
  const loadingRef = useRef(false);

  // Fetch product statistics
  const fetchProductStats = async () => {
    setStatsLoading(true);
    try {
      const response = await productAPI.getProductStats();
      
      // Handle different response structures
      let statsData = response;
      if (response && response.data) {
        statsData = response.data;
      }
      
      // Ensure we have the expected structure
      if (statsData && typeof statsData === 'object') {
        setStats({
          product_count: statsData.product_count || statsData.total_products || 0,
          available_quantity: statsData.available_quantity || statsData.total_stock || 0,
          inventory_value: statsData.inventory_value || statsData.total_inventory_value || 0,
          inventory_selling_price: statsData.inventory_selling_price || statsData.total_selling_price || 0
        });
      }
    } catch (error) {
      console.error('Error fetching product stats:', error);
      // Keep default values on error
    } finally {
      setStatsLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(searchQuery);
      setCurrentPage(1); // Reset to first page when searching
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedProduct(null);
  };

  const handleSubmitProduct = async (productData) => {
    try {
      setLoading(true);

      // Call the API to create the product
      await productAPI.createProduct(productData);

      // Refresh the products list and stats (loadProducts will fetch stats when shouldFetchStats=true)
      await loadProducts(true, true);
      setIsModalOpen(false);
      toast.success("Product created successfully!");
    } catch (error) {
      console.error("Error creating product:", error);
      handleFormattedValidationErrors(error, "Failed to create product. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitEdit = async (productData) => {
    try {
      setLoading(true);

      // Call the API to update the product
      await productAPI.updateProduct(selectedProduct.id, productData);

      // Refresh the products list and stats (loadProducts will fetch stats when shouldFetchStats=true)
      await loadProducts(true, true);
      setIsEditModalOpen(false);
      setSelectedProduct(null);
      toast.success("Product updated successfully!");
    } catch (error) {
      console.error("Error updating product:", error);
      handleFormattedValidationErrors(error, "Failed to update product. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Load products from API
  const loadProducts = async (shouldFetchStats = false, forceLoad = false) => {
    // Prevent duplicate calls
    if (loadingRef.current && !forceLoad) {
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      const params = {
        page: currentPage,
        page_size: itemsPerPage,
        search: searchTerm,
      };
      
      const response = await productAPI.getProductsList(params);
      
      setProducts(response.results || []);
      setFilteredProducts(response.results || []);
      setTotalCount(response.count || 0);
      
      // Only fetch stats if explicitly requested
      if (shouldFetchStats) {
        await fetchProductStats();
      }
    } catch (error) {
      console.error("Error loading products:", error);
      // Fallback to localStorage for development
      const stored = localStorage.getItem("products");
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setProducts(parsed);
          setFilteredProducts(parsed);
          setTotalCount(parsed.length);
        } catch {
          setProducts([]);
          setFilteredProducts([]);
          setTotalCount(0);
        }
      }
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  // Load products and stats on component mount and when dependencies change
  useEffect(() => {
    // Only fetch stats on initial load (when searchTerm is empty and page is 1)
    const shouldFetchStats = searchTerm === "" && currentPage === 1;
    
    // Skip if this is a duplicate call during initial mount
    if (!initialLoadDone.current && shouldFetchStats) {
      initialLoadDone.current = true;
    }
    
    loadProducts(shouldFetchStats);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm]);

  // Sorting only (no client-side filtering since API handles search)
  useEffect(() => {
    let sorted = [...products];

    if (["name", "category"].includes(sortField)) {
      sorted.sort((a, b) =>
        sortDirection === "asc"
          ? (a[sortField] || "").localeCompare(b[sortField] || "")
          : (b[sortField] || "").localeCompare(a[sortField] || "")
      );
    } else if (["avg_buying_price", "avg_selling_price", "in_stock"].includes(sortField)) {
      sorted.sort((a, b) =>
        sortDirection === "asc"
          ? (parseFloat(a[sortField]) || 0) - (parseFloat(b[sortField]) || 0)
          : (parseFloat(b[sortField]) || 0) - (parseFloat(a[sortField]) || 0)
      );
    } else {
      sorted.sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
    }

    setFilteredProducts(sorted);
  }, [products, sortField, sortDirection]);

  // CRUD handlers

  const handleDeleteProduct = async (id) => {
    try {
      setLoading(true);
      await productAPI.deleteProduct(id);
      // Refresh the products list and stats (loadProducts will fetch stats when shouldFetchStats=true)
      await loadProducts(true, true);
      setShowDeleteModal(false);
      setDeletingProduct(null);
      toast.success("Product deleted successfully!");
    } catch (error) {
      console.error("Error deleting product:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to delete product. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const openViewModal = (p) => {
    navigate(`/product/${p.id}`);
  };

  // Helpers
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const currentProducts = filteredProducts; // API handles pagination

  return (
    <main className="products-page">
      <div className="products-page-container products-layout">
        <div className="products-top">
          {/* Header */}
          <div className="products-header">
            <div className="products-title">
              <h1>Products</h1>
              <p>Manage your product catalog and inventory levels.</p>
            </div>
            <button
              className="add-product-btn"
              onClick={() => setIsModalOpen(true)}
            >
              <Plus className="add-product-icon" /> Add Product
            </button>
          </div>

          {/* Summary */}
          <div className="products-summary">
            <ProductCard
              title="Total Products"
              value={statsLoading ? (
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                (stats.product_count || 0).toString()
              )}
              icon={TotalProductsIcon}
            />
            <ProductCard
              title="Total Stock"
              value={statsLoading ? (
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                (stats.available_quantity || 0).toString()
              )}
              icon={TotalStockIcon}
            />
            <ProductCard
              title="Total Inventory Value"
              value={statsLoading ? (
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                `€${(stats.inventory_value || 0).toFixed(2)}`
              )}
              icon={TotalInventoryValueIcon}
            />
            <ProductCard
              title="Selling Price"
              value={statsLoading ? (
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                `€${(stats.inventory_selling_price || 0).toFixed(2)}`
              )}
              icon={SellingPriceIcon}
            />
          </div>

          {/* Search */}
          <div className="products-content">
            <SearchBar
              placeholder="Search products by name, ID, or category"
              onSearch={handleSearch}
            />
          </div>
        </div>

        {/* Table area (scrollable) */}
        <div className="products-table-wrapper">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <ProductTable
              products={currentProducts}
              onView={openViewModal}
              onEdit={handleEdit}
              onDelete={(product) => {
                setDeletingProduct(product);
                setShowDeleteModal(true);
              }}
            />
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="products-pagination-sticky">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPrevious={handlePrevious}
              onNext={handleNext}
            />
          </div>
        )}
      </div>

      <AddProductModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitProduct}
      />

      <EditProductModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSubmit={handleSubmitEdit}
        product={selectedProduct}
      />

      {/* Delete Modal */}
      {showDeleteModal && deletingProduct && (
        <>
          <div className="modal-overlay">
            <div className="delete-modal">
              <div className="modal-icon">
                <img src={DeleteIcon} alt="Delete" />
              </div>
              <h3 className="modal-title">Delete Product</h3>
              <p className="modal-message">
                Are you sure you want to delete product{" "}
                <b>{deletingProduct.name}</b>?
                <br />
                This action cannot be undone.
              </p>
              <div className="modal-actions">
                <button
                  className="modal-cancel-btn"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingProduct(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="modal-delete-btn"
                  onClick={() => handleDeleteProduct(deletingProduct.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </main>
  );
}
